import { TrackEventType } from '@orbit/api-types'
import api from './api'

const PENDING_KEY = 'orbit.analytics.pending'
const DEVICE_ID_KEY = 'orbit.analytics.deviceId'

type PendingEvent = {
	eventType: string
	data?: Record<string, unknown>
}

const generateUuid = (): string => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID()
	}
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
		const r = (Math.random() * 16) | 0
		const v = c === 'x' ? r : (r & 0x3) | 0x8
		return v.toString(16)
	})
}

/** Stable for the tab when storage is unavailable, so one session is one device. */
let sessionDeviceId: string | null = null

const getDeviceId = (): string => {
	try {
		let id = localStorage.getItem(DEVICE_ID_KEY)
		if (!id) {
			id = generateUuid()
			localStorage.setItem(DEVICE_ID_KEY, id)
		}
		return id
	} catch {
		// Blocked site data (private mode, hardened settings) throws on access.
		// This runs on the login request path, so it must never be the reason a
		// sign-in fails — degrade to a per-session id instead.
		sessionDeviceId ??= generateUuid()
		return sessionDeviceId
	}
}

const getAppVersion = (): string => {
	return import.meta.env.VITE_APP_VERSION ?? 'dev'
}

/**
 * Device context for this browser. Exported because the login, join and create
 * requests carry it as an `analytics` block: the server writes those events
 * (sign-in, `trip_joined`, `*_created`), so the webapp never names them itself.
 */
export const getDeviceContext = (): Omit<TrackEventType, 'eventType' | 'data'> => {
	return {
		versionName: getAppVersion(),
		platform: 'web',
		osVersion: navigator.userAgent,
		deviceModel: navigator.platform || undefined,
		locale: navigator.language || 'en-US',
		deviceType: undefined,
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		deviceId: getDeviceId(),
	}
}

const loadPending = (): PendingEvent[] => {
	try {
		const raw = localStorage.getItem(PENDING_KEY)
		if (!raw) return []
		const parsed = JSON.parse(raw)
		return Array.isArray(parsed) ? parsed : []
	} catch {
		return []
	}
}

const savePending = (events: PendingEvent[]): void => {
	try {
		localStorage.setItem(PENDING_KEY, JSON.stringify(events))
	} catch {
		// Best-effort: ignore quota/availability errors when persisting pending events
	}
}

const queueEvent = (event: PendingEvent): void => {
	const pending = loadPending()
	pending.push(event)
	savePending(pending)
}

const clearPending = (): void => {
	localStorage.removeItem(PENDING_KEY)
}

const send = async (event: PendingEvent): Promise<boolean> => {
	const body: TrackEventType = {
		...getDeviceContext(),
		eventType: event.eventType,
		data: event.data,
	}
	try {
		await api.post('/analytics/track-event', body)
		return true
	} catch {
		return false
	}
}

export const trackEvent = (eventType: string, data?: Record<string, unknown>): void => {
	const event: PendingEvent = { eventType, data }
	void send(event).then(success => {
		if (!success) queueEvent(event)
	})
}

export const flushPendingEvents = (): void => {
	const pending = loadPending()
	if (pending.length === 0) return
	clearPending()
	for (const event of pending) {
		void send(event).then(success => {
			if (!success) queueEvent(event)
		})
	}
}
